/**
 * Numéro 5 du programme pédagogique ETM — Notions diverses
 *
 * Thème ETM officiel : Notions diverses (code thème D).
 * Couvre : permis A1/A2/A, système de points, assurance, papiers obligatoires,
 * contraventions et délits, alcoolisme et stupéfiants au plan administratif.
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, BookOpen, Users } from 'lucide-react';

export const notionsDiversesEtmIssue = {
  // META
  slug: 'notions-diverses',
  themeCode: 'D',
  issueNumber: 5,
  totalIssues: 9,
  category: "L'administratif moto",
  shortTitle: 'Notions diverses',
  title: 'Permis, points, assurance',
  kicker: 'Numéro 5 du programme pédagogique ETM',
  subtitle:
    "Tout l'administratif que tu dois maîtriser pour rester dans les clous : les 3 catégories de permis moto, le système de points, les papiers obligatoires, et les sanctions qui peuvent te coûter ton permis du jour au lendemain.",
  heroImage: '/img/magazines/notions-diverses.jpg',
  heroImageAlt: 'Bureau de travail avec ordinateur portable et papiers — illustration de l’administratif lié au permis moto',
  readTimeMin: 13,

  // SEO
  seoDescription:
    "Tout sur l'administratif moto pour l'ETM 2026 : permis A1 A2 A, système à points, assurance obligatoire, papiers à bord, contraventions et délits routiers. Cours + QCM auto-évaluant.",
  seoKeywords:
    'permis A2 moto, système points permis, assurance moto obligatoire, contravention moto 2026, délit routier, permis probatoire',

  // ÉDITO
  edito: {
    title: "L'administratif, ce n'est pas glamour — mais c'est ton bouclier",
    body: "Un permis peut se perdre en <strong>3 secondes</strong> : un grand excès, un téléphone au volant, un dépassement à 70 cm d'un cycliste. La différence entre un motard libre et un motard à pied, ce n'est pas une question de chance : c'est la connaissance précise du système. Ce numéro t'arme avec les <strong>4 piliers administratifs</strong> qu'un motard ECSR-formé doit connaître par cœur — et que l'ETM 2026 te demandera de maîtriser.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  // SECTIONS
  sections: [
    // === CHAPITRE 1 — Les permis moto ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Les 3 permis moto : A1, A2, A',
      paragraphs: [
        "Depuis la réforme européenne de 2013, le permis moto est séquencé en 3 catégories progressives. Voici ce qu'il faut savoir pour 2026 :",
        "<strong>Permis A1 (16 ans, motos légères)</strong>. Accès aux motos jusqu'à 125 cm³ et 11 kW (15 ch). Vitesse max : celle de la machine. Formation : code ETM + 20h pratique min (8h plateau, 12h circulation). Sanction si conduite hors catégorie : 1 500 € + immobilisation du véhicule.",
        "<strong>Permis A2 (18 ans, bridé 35 kW)</strong>. Accès aux motos jusqu'à 35 kW (47,5 ch), avec rapport puissance/poids ≤ 0,2 kW/kg. C'est le permis par défaut pour les jeunes adultes depuis 2016. Durée : <strong>2 ans minimum</strong> avant de pouvoir passer en A.",
        "<strong>Permis A (20 ans, via passerelle A2 → A)</strong>. Accès aux motos sans limitation de puissance. Depuis le 19 janvier 2013, il n'existe <strong>plus d'accès direct</strong> au permis A : tu dois OBLIGATOIREMENT être titulaire du permis A2 depuis 2 ans minimum + suivre une formation passerelle de <strong>7 heures</strong> (2h théorie + 2h plateau + 3h circulation). Pas de nouvel ETM ni d'examen final.",
        "À noter : depuis 2020, l'<strong>ETM (Épreuve Théorique Moto)</strong> est spécifique et distincte du code voiture. Elle teste 9 thèmes (ceux du Magazine ETM à toi !) sur 40 questions, score minimal 35/40.",
      ],
      keyPoints: [
        "A1 = 125 cm³ max, dès 16 ans",
        "A2 = 35 kW max, 18 ans, obligatoire 2 ans",
        "A = passerelle 7h après 2 ans de A2",
        "ETM = code moto spécifique depuis 2020",
      ],
    },

    // === SAVIEZ-VOUS ===
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "Avant 2016, les titulaires du permis B (voiture) avec 2 ans d'ancienneté pouvaient piloter une 125 cm³ après une <strong>simple formation de 7 heures</strong> (sans examen). Depuis cette date, la formation reste obligatoire mais elle ne suffit plus pour les motos équivalentes 125 cm³ acquises après 2016. Pour les nouvelles motos, il faut un véritable A1 ou A2. Conséquence : un motard « 125 » avec uniquement la formation 7h ne peut conduire qu'une moto d'avant 2016. Piège classique à l'ETM.",
    },

    // === CHAPITRE 2 — Système à points ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Le système à points : 12 points, comment les conserver',
      paragraphs: [
        "<strong>Règle de base</strong> : tout titulaire du permis dispose d'un <strong>capital de 12 points</strong> (6 points seulement pendant la période probatoire). Les points se reconstituent avec le temps OU avec un stage volontaire de sensibilisation.",
        "<strong>Période probatoire</strong> : 3 ans (2 ans si AAC voiture). Capital initial : 6 points. Si tu ne perds aucun point : +2 points/an automatiques (3 ans = retour aux 12). Si tu perds 3 points ou plus en une seule infraction : <strong>stage obligatoire</strong> dans les 4 mois.",
        "<strong>Reconstitution automatique des points</strong> : 6 mois sans nouvelle infraction = +1 point (après contravention classée 1ère ou 2ème cat). 2 ans = restitution totale (après contravention 3ème ou 4ème). 3 ans = restitution après délit. 10 ans = effacement permanent.",
        "<strong>Stage de sensibilisation</strong> : 2 jours, environ 280 €. Récupération de <strong>4 points maximum</strong>. Possible 1 fois par an. Volontaire OU obligatoire (probatoire avec 3 pts ou plus perdus). À noter : tu ne peux pas récupérer plus que ton solde ne le permet (max 12 au total).",
        "<strong>Perte totale du permis (0 point)</strong> : invalidation. Tu dois rendre ton permis dans les 10 jours. Délai de carence : 6 mois (1 an si ton permis a moins de 3 ans). Puis nouveau passage code + plateau + circulation.",
      ],
      keyPoints: [
        '12 points pour un permis normal, 6 en probatoire',
        '+1 point après 6 mois sans infraction',
        'Stage = max 4 points récupérés, 1x par an',
        '0 point = invalidation + 6 mois min de carence',
      ],
    },

    // === PIÈGE ===
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Je fais un stage chaque année, donc je peux me permettre des infractions régulières. » <strong>FAUX et dangereux.</strong> Premièrement, tu ne peux faire qu'<strong>UN stage par an</strong>. Deuxièmement, en période probatoire si tu perds plus de 3 points en une seule infraction, le stage est obligatoire ET tu paies. Troisièmement, certaines infractions (délits, alcool, stupéfiants) entraînent une <strong>suspension administrative immédiate</strong> indépendamment de ton solde de points. Conclusion : le stage est un filet de sécurité, pas un mode de vie.",
    },

    // === CHAPITRE 3 — Assurance ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: "L'assurance moto : 3 niveaux, 1 obligation légale",
      paragraphs: [
        "En France, l'<strong>assurance responsabilité civile (RC)</strong> est obligatoire pour tout véhicule motorisé circulant sur la voie publique (article L211-1 du Code des assurances). Même une moto au garage doit être assurée si elle est immatriculée.",
        "<strong>3 niveaux de couverture</strong> :",
        "<strong>1. RC (au tiers)</strong>. Le minimum légal. Couvre les dommages causés aux tiers (voiture endommagée, piéton blessé). <strong>NE COUVRE PAS</strong> ta moto ni tes propres blessures. Prix moyen : 200-400 €/an.",
        "<strong>2. RC + Vol/Incendie (tiers étendu)</strong>. Ajoute la couverture vol et incendie de TA moto. Recommandé pour les motos > 3 000 €. Prix : 400-700 €/an.",
        "<strong>3. Tous risques</strong>. Couvre TOUT : tes dommages, ta moto, ton pilote (toi). Le bon choix pour les motos récentes (< 3 ans). Prix : 700-1 500 €/an.",
        "<strong>Sanction si défaut d'assurance</strong> : 3 750 € d'amende, suspension de permis jusqu'à 3 ans, confiscation du véhicule, peine de prison jusqu'à 6 mois. C'est considéré comme un délit, pas une contravention.",
        "<strong>Bonus / Malus</strong> : tu démarres au coefficient 1,00. Chaque année sans sinistre responsable : -5 % (coeff 0,95, 0,90, etc., jusqu'à 0,50 minimum). Chaque sinistre responsable : +25 % (1,25, 1,56…). Après 2 ans sans sinistre responsable, retour au coeff de départ possible.",
      ],
      keyPoints: [
        'RC = obligatoire, point. Même moto au garage',
        'Tiers étendu = bon choix à partir de 3 000 €',
        'Défaut d’assurance = 3 750 € + délit',
        'Bonus -5 %/an, malus +25 % par accident responsable',
      ],
    },

    // === CONSEIL DU MONITEUR ===
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« L'erreur n°1 des jeunes motards, c'est de prendre une RC à 200 € sur une 600 cm³ à 8 000 €. À la première chute solo, la moto est en miettes ET tu paies tout. Mon conseil : sur une moto neuve ou récente, prends <strong>tous risques avec une franchise vol/dommage élevée</strong> (1 000-1 500 €) — ça réduit la prime d'environ 30 %. Tu paies plus en cas de petègnotte, mais tu es protégé si la moto est volée ou si tu fais un gros sinistre. »",
    },

    // === CHAPITRE 4 — Papiers obligatoires ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 4',
      title: 'Les 5 papiers obligatoires à bord (et leurs amendes)',
      paragraphs: [
        "En cas de contrôle routier, le motard doit pouvoir présenter <strong>5 documents</strong>. Manquer un seul = amende.",
        "<strong>1. Permis de conduire</strong> (catégorie A1/A2/A correspondant à ta moto). Sanction si oubli : 11 € (forfaitaire), 38 € si majoration. Si tu n'as pas de permis du tout : 15 000 € + 1 an de prison + confiscation.",
        "<strong>2. Certificat d'immatriculation (carte grise)</strong>. Original ou photocopie certifiée acceptée depuis 2017. Oubli : 11 €. Document falsifié ou non transcrit : 750 € + délit.",
        "<strong>3. Attestation d'assurance</strong> (papillon vert) collante sur le pare-brise ou générale dans une pochette. Depuis 2024, le contrôle se fait également via le <strong>Fichier des Véhicules Assurés (FVA)</strong>. Oubli du papier : 35 €. Absence réelle d'assurance : 3 750 € + délit (voir chap. 3).",
        "<strong>4. Contrôle technique (depuis le 15 avril 2024 pour les motos &amp; 2/3 roues catégorie L)</strong>. 1ère visite dans les 6 mois avant le 5ᵉ anniversaire de mise en circulation, puis périodicité <strong>3 ans</strong> si favorable. <strong>80 points</strong> de contrôle. Sanction : amende forfaitaire 135 € (jusqu'à 750 €) + possible immobilisation. Le procès-verbal doit être dans ton portefeuille ou ton téléphone (version numérique acceptée).",
        "<strong>5. Carte d'identité OU passeport</strong>. Tout conducteur doit pouvoir prouver son identité lors d'un contrôle. Sanction si refus : retenue jusqu'à vérification (max 4h).",
      ],
      keyPoints: [
        '5 documents à bord : permis, carte grise, attestation, CT, identité',
        'CT moto = obligatoire depuis avril 2024',
        'Attestation = aussi vérifiée via le FVA',
        "Défaut total = jusqu'à 15 000 € selon le doc manquant",
      ],
    },

    // === TÉMOIGNAGE ===
    {
      type: 'story',
      icon: Users,
      title: "Le témoignage : « J'ai perdu mon permis pour un téléphone »",
      body: "« 22 ans, permis probatoire depuis 14 mois. Je tenais mon portable pour suivre mon GPS (le support était cassé). Un motard de la police m'a vu à un feu rouge. Résultat : 135 € + <strong>3 points</strong>. Or j'avais déjà perdu 1 point pour excès de vitesse 6 mois avant. Total : 4 points sur mes 6 points probatoires. Je suis passé à 2 points. J'ai payé 280 € de stage pour en récupérer 4, et j'ai compris que le téléphone à moto, c'est un suicide professionnel. Depuis, j'ai un Cardo Bluetooth dans le casque — 100 €, c'est mon meilleur achat moto. » <em>— Sami, 23 ans</em>",
    },
  ],

  // CHIFFRE
  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '70 000',
    middle: 'permis invalidés',
    label: "par an en France pour défaut de points",
    source: 'Ministère de l’Intérieur — Délégation à la Sécurité routière 2024',
    accent: '#16a34a',
  },

  // GLOSSAIRE
  glossary: [
    { term: 'ETM', def: "Épreuve Théorique Moto, spécifique aux deux-roues depuis 2020. 40 questions sur 9 thèmes, score minimal 35/40." },
    { term: 'Permis A1', def: "Permis accès dès 16 ans, motos ≤ 125 cm³ et ≤ 11 kW. Inclut tricycles motorisés sous conditions." },
    { term: 'Permis A2', def: "Permis 18 ans, motos ≤ 35 kW avec ratio P/poids ≤ 0,2 kW/kg. Obligatoire avant l'accès au A." },
    { term: 'Permis A', def: "Permis sans limitation de puissance, accessible 2 ans après le A2 via une formation passerelle de 7 heures." },
    { term: 'Période probatoire', def: "3 ans (ou 2 en AAC) post-obtention du permis, avec capital de 6 points, +2/an automatique si pas d'infraction." },
    { term: 'Stage de sensibilisation', def: "Formation de 2 jours (~280 €) permettant de récupérer jusqu'à 4 points. 1 fois par an maximum." },
    { term: 'Bonus-malus', def: "Système d'ajustement de la prime d'assurance : -5 % par an sans sinistre, +25 % par sinistre responsable." },
    { term: 'FVA', def: "Fichier des Véhicules Assurés. Base nationale permettant aux forces de l'ordre de vérifier l'assurance sans demander le papier." },
    { term: 'CT moto', def: "Contrôle technique des motos &amp; 2/3 roues, obligatoire depuis le 15 avril 2024 (décret 2023-974). 1ère visite à ~5 ans (dans les 6 mois précédant le 5ᵉ anniversaire), puis tous les 3 ans. 80 points de contrôle." },
    { term: 'Invalidation', def: "Annulation administrative du permis suite à perte totale de points. Délai de carence de 6 mois à 1 an avant nouveau passage." },
  ],

  // QCM
  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur l'administratif moto tirées de la banque officielle ETM. Tu dois en réussir <strong>au moins 8 sur 10</strong> pour valider ta maîtrise du thème.",
    themeCode: 'D',
    questionsCount: 10,
    passingScore: 8,
  },

  // NEXT
  nextIssue: {
    slug: 'mecanique',
    number: 6,
    title: "Une moto bien réglée, c'est ta vie",
    short: 'La mécanique & l’entretien',
  },
};

export default notionsDiversesEtmIssue;
